import React, { useState, useEffect } from "react";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonCard,
  IonCardContent,
  IonInput,
  IonButton,
  IonBadge,
  IonLoading,
  IonAlert,
  IonToast,
  IonIcon,
  IonText,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonButtons,
} from "@ionic/react";
import {
  timeOutline,
  trophyOutline,
  lockClosedOutline,
  saveOutline,
  arrowBackOutline,
} from "ionicons/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useParams, useHistory } from "react-router-dom";
import { useIonRouter } from "@ionic/react";
import { format } from "date-fns";
import api from "@/services/api";
import TeamWithForm from "@/components/TeamWithForm";
import { useI18n } from "@/i18n";

interface Match {
  id: number;
  matchday: number;
  home_team_id: number;
  home_team_name: string;
  home_team_short: string;
  home_team_logo?: string;
  away_team_id: number;
  away_team_name: string;
  away_team_short: string;
  away_team_logo?: string;
  home_score?: number;
  away_score?: number;
  kickoff_time: string;
  status: string;
  is_deadline_passed: boolean;
  can_predict: boolean;
  prediction?: {
    predicted_home_score: number;
    predicted_away_score: number;
    points_awarded: number;
    scoring_type: string;
    is_locked: boolean;
  };
}

interface MatchdayData {
  matchday: number;
  season_id: number;
  matches: Match[];
  statistics: {
    total_predictors: number;
    total_predictions: number;
    finished_matches: number;
  };
}

const getScoringColor = (type: string): string => {
  switch (type) {
    case "exact_score": return "var(--app-score-exact)";
    case "goal_difference": return "var(--app-score-diff)";
    case "tendency": return "var(--app-score-tendency)";
    default: return "var(--app-score-wrong)";
  }
};

const Matchday: React.FC = () => {
  const { seasonId, matchday } = useParams<{ seasonId: string; matchday: string }>();
  const queryClient = useQueryClient();
  const history = useHistory();
  const ionRouter = useIonRouter();
  const { t } = useI18n();

  const [predictions, setPredictions] = useState<Record<number, { home: string; away: string }>>({});
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [viewMode, setViewMode] = useState<"predict" | "results">("predict");

  const handleBackToMatches = () => {
    ionRouter.push("/matches?tab=matchdays");
    setTimeout(() => history.push("/matches?tab=matchdays"), 50);
    setTimeout(() => {
      if (window.location.pathname === "/matches") window.location.reload();
    }, 100);
  };

  useEffect(() => {
    if (seasonId && matchday) queryClient.removeQueries(["matchday", seasonId, matchday]);
  }, [seasonId, matchday, queryClient]);

  if (!seasonId || !matchday) {
    return (
      <IonPage>
        <IonHeader><IonToolbar><IonTitle>{t("matchdayTitle")}</IonTitle></IonToolbar></IonHeader>
        <IonContent className="ion-padding"><p>{t("loadingMatchday")}</p></IonContent>
      </IonPage>
    );
  }

  const { data: matchdayData, isLoading, error } = useQuery<MatchdayData>(
    ["matchday", seasonId, matchday],
    async () => (await api.get(`/api/matchdays/${seasonId}/${matchday}`)).data,
    { refetchInterval: 60000, enabled: !!(seasonId && matchday) }
  );

  useEffect(() => {
    if (matchdayData?.matches) {
      const init: Record<number, { home: string; away: string }> = {};
      matchdayData.matches.forEach((m) => {
        init[m.id] = m.prediction
          ? { home: m.prediction.predicted_home_score.toString(), away: m.prediction.predicted_away_score.toString() }
          : { home: "", away: "" };
      });
      setPredictions(init);
    }
  }, [matchdayData]);

  const saveMutation = useMutation(
    async (data: Array<{ matchId: number; predictedHomeScore: number; predictedAwayScore: number }>) =>
      (await api.post("/api/predictions/batch", { predictions: data })).data,
    {
      onSuccess: () => { setShowSaveToast(true); queryClient.invalidateQueries(["matchday", seasonId, matchday]); },
      onError: (e: any) => { setErrorMessage(e.response?.data?.error || t("failedToSavePredictions")); setShowErrorAlert(true); },
    }
  );

  const handleChange = (matchId: number, type: "home" | "away", value: string) => {
    setPredictions((prev) => ({ ...prev, [matchId]: { ...prev[matchId], [type]: value.replace(/[^\d]/g, "") } }));
  };

  const handleSave = () => {
    if (!matchdayData) return;
    const data = matchdayData.matches
      .filter((m) => !m.is_deadline_passed)
      .map((m) => {
        const p = predictions[m.id];
        return { matchId: m.id, predictedHomeScore: parseInt(p?.home || "0", 10) || 0, predictedAwayScore: parseInt(p?.away || "0", 10) || 0 };
      });
    if (!data.length) { setErrorMessage(t("enterAtLeastOnePrediction")); setShowErrorAlert(true); return; }
    saveMutation.mutate(data);
  };

  const fmtTime = (t: string) => format(new Date(t), "EEE, MMM dd - HH:mm");

  if (isLoading) {
    return (
      <IonPage>
        <IonHeader><IonToolbar>
          <IonButtons slot="start"><IonButton onClick={handleBackToMatches}><IonIcon icon={arrowBackOutline} /></IonButton></IonButtons>
          <IonTitle>{t("loadingMatchday")}</IonTitle>
        </IonToolbar></IonHeader>
        <IonContent><IonLoading isOpen message={t("loadingMatchdayData")} /></IonContent>
      </IonPage>
    );
  }

  if (error || !matchdayData) {
    return (
      <IonPage>
        <IonHeader><IonToolbar>
          <IonButtons slot="start"><IonButton onClick={handleBackToMatches}><IonIcon icon={arrowBackOutline} /></IonButton></IonButtons>
          <IonTitle>{t("errorTitle")}</IonTitle>
        </IonToolbar></IonHeader>
        <IonContent className="ion-padding"><p>{t("errorLoadingMatchday")}</p></IonContent>
      </IonPage>
    );
  }

  const hasFinished = matchdayData.matches.some((m) => m.status === "finished");

  return (
    <IonPage key={`matchday-${seasonId}-${matchday}`}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleBackToMatches}><IonIcon icon={arrowBackOutline} /></IonButton>
          </IonButtons>
          <IonTitle>{t("matchdayTitle")} {matchdayData.matchday}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: "12px 16px" }}>
          {/* Stats row */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
            <div className="stat-pill" style={{ flex: 1 }}>
              <strong className="tabular-nums">{matchdayData.statistics.total_predictors}</strong>
              <span>{t("predictorsLabel")}</span>
            </div>
            <div className="stat-pill" style={{ flex: 1 }}>
              <strong className="tabular-nums">{matchdayData.statistics.total_predictions}</strong>
              <span>{t("predictions")}</span>
            </div>
            <div className="stat-pill" style={{ flex: 1 }}>
              <strong className="tabular-nums">{matchdayData.statistics.finished_matches}/{matchdayData.matches.length}</strong>
              <span>{t("finishedLabel")}</span>
            </div>
          </div>

          {hasFinished && (
            <IonSegment value={viewMode} onIonChange={(e) => setViewMode(e.detail.value as "predict" | "results")} style={{ marginBottom: "12px" }}>
              <IonSegmentButton value="predict"><IonLabel>{t("makePredictions")}</IonLabel></IonSegmentButton>
              <IonSegmentButton value="results"><IonLabel>{t("viewResults")}</IonLabel></IonSegmentButton>
            </IonSegment>
          )}

          {/* Match cards */}
          {matchdayData.matches.map((match) => (
            <IonCard key={match.id} style={{ margin: "0 0 10px 0" }}>
              <IonCardContent style={{ padding: "12px 14px" }}>
                {/* Teams header */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                  <TeamWithForm teamId={match.home_team_id} teamName={match.home_team_name} teamShort={match.home_team_short} size="5" className="ion-text-center" />
                  <div style={{ width: "40px", textAlign: "center" }}>
                    {match.status === "finished" && match.home_score !== undefined && match.away_score !== undefined ? (
                      <strong className="tabular-nums" style={{ fontSize: "16px" }}>{match.home_score} : {match.away_score}</strong>
                    ) : (
                      <IonText color="medium" style={{ fontSize: "13px" }}>vs</IonText>
                    )}
                  </div>
                  <TeamWithForm teamId={match.away_team_id} teamName={match.away_team_name} teamShort={match.away_team_short} size="5" className="ion-text-center" />
                </div>

                {/* Kickoff + status */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px", fontSize: "12px", color: "var(--ion-color-medium)" }}>
                  <IonIcon icon={timeOutline} style={{ fontSize: "14px" }} />
                  <span>{fmtTime(match.kickoff_time)}</span>
                  <IonBadge
                    style={{
                      marginLeft: "auto",
                      fontSize: "10px",
                      "--background":
                        match.status === "finished" ? "var(--ion-color-success)" :
                        match.status === "live" ? "var(--ion-color-warning)" :
                        "var(--ion-color-medium)",
                    }}
                  >
                    {match.status.toUpperCase()}
                  </IonBadge>
                </div>

                {/* Prediction area */}
                {viewMode === "predict" ? (
                  Boolean(match.is_deadline_passed) ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0", color: "var(--ion-color-medium)", fontSize: "13px" }}>
                      <IonIcon icon={lockClosedOutline} />
                      <span>
                        {match.prediction
                          ? `${match.home_team_name} ${match.prediction.predicted_home_score} : ${match.prediction.predicted_away_score} ${match.away_team_name}`
                          : t("noPredictionMade")}
                      </span>
                    </div>
                  ) : (
                    <div className="prediction-row">
                      <span className="team-label">{match.home_team_name}</span>
                      <IonInput
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max="20"
                        value={predictions[match.id]?.home ?? ""}
                        onIonInput={(e) => handleChange(match.id, "home", e.detail.value ?? "")}
                        onIonFocus={(e) => { const el = e.target; setTimeout(() => { const n = el.querySelector("input"); if (n) n.select(); }, 0); }}
                        placeholder="0"
                        clearInput={false}
                        fill="outline"
                        className="score-input"
                      />
                      <span className="separator">:</span>
                      <IonInput
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max="20"
                        value={predictions[match.id]?.away ?? ""}
                        onIonInput={(e) => handleChange(match.id, "away", e.detail.value ?? "")}
                        onIonFocus={(e) => { const el = e.target; setTimeout(() => { const n = el.querySelector("input"); if (n) n.select(); }, 0); }}
                        placeholder="0"
                        clearInput={false}
                        fill="outline"
                        className="score-input"
                      />
                      <span className="team-label">{match.away_team_name}</span>
                    </div>
                  )
                ) : (
                  match.prediction && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "6px 0" }}>
                      <IonIcon icon={trophyOutline} style={{ fontSize: "18px", color: "var(--app-gold)" }} />
                      <span className="tabular-nums" style={{ fontSize: "13px" }}>
                        {match.home_team_name} {match.prediction.predicted_home_score} : {match.prediction.predicted_away_score} {match.away_team_name}
                      </span>
                      {match.prediction.points_awarded > 0 && (
                        <span style={{
                          marginLeft: "auto",
                          fontSize: "11px",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: "6px",
                          color: "#fff",
                          background: getScoringColor(match.prediction.scoring_type),
                        }}>
                          {match.prediction.points_awarded} {t("points")}
                        </span>
                      )}
                    </div>
                  )
                )}
              </IonCardContent>
            </IonCard>
          ))}

          {/* Save button */}
          {viewMode === "predict" && (
            <IonButton
              expand="block"
              onClick={handleSave}
              disabled={saveMutation.isLoading}
              className="save-btn"
              style={{ "--border-radius": "10px", marginTop: "8px" }}
            >
              <IonIcon icon={saveOutline} slot="start" />
              {saveMutation.isLoading ? t("saving") : t("saveAllPredictions")}
            </IonButton>
          )}
        </div>

        <IonToast isOpen={showSaveToast} onDidDismiss={() => setShowSaveToast(false)} message={t("predictionsSaved")} duration={2000} color="success" />
        <IonAlert isOpen={showErrorAlert} onDidDismiss={() => setShowErrorAlert(false)} header={t("errorTitle")} message={errorMessage} buttons={[t("okButton")]} />
      </IonContent>
    </IonPage>
  );
};

export default Matchday;
